import { ProductReferenceType } from 'vrlcm-gui-types';

export namespace LcopsApiUri {
  const BASE_URL = '/lcm/lcops/api';
  const BASE_URL_V2 = `${BASE_URL}/v2`;

  export namespace ProductPolicy {
    export const PRODUCTS_POLICY_URL = `${BASE_URL}/policy/products`;

    export const GET_ALL_PRODUCTS_POLICY_URL = (
      vcfEnabledEnvironment?: boolean
    ) => {
      let url = PRODUCTS_POLICY_URL;
      url += `?includeAllProducts=true`;
      if (vcfEnabledEnvironment !== undefined) {
        url += `&vcfEnabledEnvironment=${vcfEnabledEnvironment}`;
      }
      return url;
    };

    export const GET_SUPPORTED_PRODUCT_VERSIONS_URL = `${PRODUCTS_POLICY_URL}/version`;

    export const GET_PRODUCT_SIZING_GROUPS_URL = (
      productId: string,
      version: string
    ) => `${PRODUCTS_POLICY_URL}/${productId}/versions/${version}/sizingGroups`;
  }

  export const GET_ALL_PROXY_POLICY = `${BASE_URL}/policy/proxies`;

  export const LIST_KBS_URL = `${BASE_URL}/listKbs`;
  export const VALIDATE_KB = `${BASE_URL}/validateKb`;
  export const EXECUTE_KB = `${BASE_URL}/executeKb`;

  export namespace DataCenterV2 {
    export const DATACENTERS_URL = `${BASE_URL_V2}/datacenters`;

    export const DATACENTER_URL = (dataCenterVmid: string) =>
      `${DATACENTERS_URL}/${dataCenterVmid}`;

    export const DATACENTER_VMS_URL = (dataCenterVmid: string) =>
      `${DATACENTER_URL(dataCenterVmid)}/vms`;

    export const VCENTERS_URL = (dataCenterVmid: string) =>
      `${DATACENTER_URL(dataCenterVmid)}/vcenters`;

    export const GET_DATACENTER_ENVIRONMENTS_URL = (dataCenterVmid: string) =>
      `${DATACENTER_URL(dataCenterVmid)}/environments`;

    export const VCENTER_URL = (dataCenterVmid: string, vCenterName: string) =>
      `${VCENTERS_URL(dataCenterVmid)}/${vCenterName}`;

    export const HAS_VMWARESSO_URL = (
      dataCenterVmid: string,
      vCenterName: string
    ) => `${VCENTERS_URL(dataCenterVmid)}/${vCenterName}/hasVMwareSSO`;

    export const VALIDATE_VCENTER_URL = (dataCenterVmid: string) =>
      `${VCENTERS_URL(dataCenterVmid)}/validate`;

    export const GET_VCENTER_ENVIRONMENTS_URL = (
      dataCenterVmid: string,
      vCenterName: string
    ) => `${VCENTER_URL(dataCenterVmid, vCenterName)}/environments`;

    export const VCENTER_DATA_COLLECTION_URL = (
      dataCenterVmid: string,
      vCenterName: string
    ) => `${VCENTER_URL(dataCenterVmid, vCenterName)}/data-collection`;

    export const IMPORT_VCENTERS_URL = (dataCenterVmid: string) =>
      `${VCENTERS_URL(dataCenterVmid)}/import`;

    export const VALIDATE_IMPORT_VCENTERS_URL = (dataCenterVmid: string) =>
      `${IMPORT_VCENTERS_URL(dataCenterVmid)}/validate`;
  }

  // Geo Location URLs
  export const GET_GEOLOCATION_URL = (pattern: string) =>
    `${BASE_URL}/geolocation/search/${pattern}`;

  export namespace Metadata {
    // Metadata URLs
    export const GET_INFRA_METADATA_URL = `${BASE_URL}/uimetadata/environment/infra`;

    export const GET_PRODUCT_PROPERTIES_METADATA_URL = `${BASE_URL}/uimetadata/productProperties`;

    export const GET_IMPORT_PROPERTIES_METADATA_URL = (
      product: string,
      vcfEnabledEnvironment?: boolean
    ) => {
      let url = `${BASE_URL}/uimetadata/products/${product}/import`;
      if (vcfEnabledEnvironment !== undefined) {
        url += `?vcfEnabledEnvironment=${vcfEnabledEnvironment}`;
      }
      return url;
    };

    export const GET_PRODUCT_METADATA_URL = `${BASE_URL}/uimetadata/products/nodes`;

    export const GET_PRODUCT_CLUSTER_VIP_METADATA_URL = (
      product: string,
      version: string
    ) =>
      `${BASE_URL}/uimetadata/products/${product}/version/${version}/clustervip`;

    export const GET_NODES_CARDINALITY_URL = (
      product: string,
      version: string
    ) =>
      `${BASE_URL}/uimetadata/products/${product}/version/${version}/cardinality`;

    export const GET_NODE_DEFAULT_PROPERTIES_METADATA_URL = (
      product: string,
      nodeType: string
    ) => `${BASE_URL}/uimetadata/products/${product}/node/${nodeType}/default`;

    export const GET_NODE_ADVANCED_PROPERTIES_METADATA_URL = (
      product: string,
      nodeType: string
    ) => `${BASE_URL}/uimetadata/products/${product}/node/${nodeType}/advance`;

    export const GET_PRODUCT_ACTION_METADATA_URL = (
      product: string,
      version: string,
      action: string
    ) =>
      `${BASE_URL}/uimetadata/products/${product}/version/${version}/action/${action}`;

    export const GET_DATA_SOURCES_META_DATA_URL = (version: string) =>
      `${BASE_URL}/uimetadata/products/vrni/${version}/datasource`;
  }

  export namespace EnvironmentV2 {
    export const ENVIRONMENTS_URL = `${BASE_URL_V2}/environments`;

    export const ENVIRONMENT_URL = (environmentId: string) =>
      `${ENVIRONMENTS_URL}/${environmentId}`;

    export const ENVIRONMENT_REQUEST_URL = (
      environmentId: string,
      requestId: string
    ) => `${ENVIRONMENT_URL(environmentId)}/requests/${requestId}`;

    export const TOGGLE_CEIP = (environmentId: string) =>
      `${ENVIRONMENT_URL(environmentId)}/toggleCeip`;

    export const TOGGLE_VCF = (environmentId: string) =>
      `${ENVIRONMENT_URL(environmentId)}/toggleVcfEnvironment`;

    export const VALIDATE_REFRESH_TOKEN = `${BASE_URL_V2}/cloudproxyenvironments/validate-refresh-token`;
  }
  export namespace Environment {
    export const ENVIRONMENTS_URL = `${BASE_URL}/environments`;

    export const PREVALIDATE_JSON_SPEC_URL = `${ENVIRONMENTS_URL}/import/prevalidate`;

    export const DELETE_ENVIRONMENT_URL = (environmentId: string) =>
      `${BASE_URL}/environments/${environmentId}/delete`;

    export const SCALE_OUT_URL = (environmentId: string, productId: string) =>
      `${ENVIRONMENTS_URL}/${environmentId}/products/${productId}/nodes`;

    export const ORGANIC_GROWTH_URL = (environmentId: string) =>
      `${ENVIRONMENTS_URL}/${environmentId}/addproducts`;

    export const GET_ENVIRONMENT_STATE_URL = (requestId: string) =>
      `${BASE_URL}/${requestId}/ui`;

    export const GET_REDEPLOY_REQUEST_DATA_URL = (requestId: string) =>
      `${ENVIRONMENTS_URL}/${requestId}/redeploy`;

    export const INTEGRATE_VMWARESSO = (environmentId: string) =>
      `${BASE_URL}/environments/${environmentId}/integrateVMwareSSO`;

    export const GET_PENDING_ENVIRONMENT_REQUESTS_URL = `${ENVIRONMENTS_URL}/pendingrequests`;

    export const GET_ALL_ENVIRONMENT_REQUESTS_URL = `${ENVIRONMENTS_URL}/requests`;

    export const GET_CLOUD_PROXY_ENVIRONMENT_REQUESTS = `${BASE_URL}/cloudproxyenvironments/requests`;

    export const DOWNLOAD_ENVIRONMENT_LOGS_URL = (environmentId: string) =>
      `${BASE_URL}/environments/${environmentId}/logs`;

    export const EXPORT_CONFIGURATION_URL = (
      environmentId: string,
      type: string
    ) => `${BASE_URL}/environments/${environmentId}/configuration?type=${type}`;

    export const TRIGGER_ALL_ENVIRONMENT_INVENTORY_SYNC_URL = `${BASE_URL}/environments/inventorysync`;

    export const TRIGGER_ENVIRONMENT_INVENTORY_SYNC_URL = (
      environmentId: string
    ) => `${BASE_URL}/environments/${environmentId}/inventorysync`;

    export const TOGGLE_HEALTH_URL = (environmentId: string, toggle: string) =>
      `${BASE_URL}/environments/${environmentId}/healthCheckToggle?toggle=${toggle}`;

    export const GET_VIDM_CLUSTER_HEALTH = `${BASE_URL}/environments/globalenvironment/products/vidm/productCardClusterHealthNotification`;

    export const GET_VIDM_CLUSTER_AUTO_RECOVERY = `${BASE_URL}/environments/globalenvironment/products/vidm/clusterAutoRecoveryStatus`;

    export const RESUME_VIDM_CLUSTER_HEALTH_NOTIFICATION = (enable: boolean) =>
      `${BASE_URL}/environments/globalenvironment/products/vidm/ClusterHealthNotification?enable=${enable}`;

    export const GET_ENVIRONMENT_VMS_FOR_DELETE_URL = (
      environmentId: string,
      deleteWindowsVMs: boolean
    ) =>
      `${BASE_URL}/environments/${environmentId}/delete?deleteWindowsVMs=${deleteWindowsVMs}`;

    export const CREATE_LOCKER_REFERENCES_FROM_JSON_URL = `${BASE_URL}/environments/createLockerReferences`;

    export const RUN_ENVIRONMENT_PRECHECK_VALIDATIONS_URL = `${BASE_URL}/environments/prevalidate`;

    export const RUN_ENVIRONMENT_PRECHECK_VALIDATIONS_AND_DEPLOY_WINDOWS_URL = `${BASE_URL}/environments/prevalidateandinstallwindows`;

    export const VRA_INTERNAL_NETWORK_RANGE_REASSIGN = (
      environmentId: string,
      productId: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/internalNetworkRange/reassign`;
  }

  export namespace ProductV2 {
    export const PRODUCTS_URL = (environmentId: string) =>
      `${BASE_URL_V2}/environments/${environmentId}/products`;

    export const PRODUCT_URL = (environmentId: string, productId: string) =>
      `${BASE_URL_V2}/environments/${environmentId}/products/${productId}`;

    export const DATA_SOURCES_URL = (
      environmentId: string,
      productId: string
    ) => `${PRODUCT_URL(environmentId, productId)}/data-sources`;

    export const VALIDATE_DATA_SOURCES_URL = (
      environmentId: string,
      productId: string
    ) => `${DATA_SOURCES_URL(environmentId, productId)}/pre-validate`;

    export const GET_DATA_SOURCE_ASSOCIATIONS_URL = (
      environmentId: string,
      productId: string,
      dsIp: string
    ) => `${DATA_SOURCES_URL(environmentId, productId)}/${dsIp}/associations`;

    export const VERTICAL_SCALE_UP_URL = (
      environmentId: string,
      productId: string
    ) => `${PRODUCT_URL(environmentId, productId)}/vertical-scale-up`;

    export const VALIDATE_VERTICAL_SCALE_UP_URL = (
      environmentId: string,
      productId: string
    ) => `${VERTICAL_SCALE_UP_URL(environmentId, productId)}/pre-validate`;

    export const GET_PRODUCT_DEPLOYED_VMS_URL = (
      environmentId: string,
      productId: string,
      getWindowsVms = false,
      tenantId?: string
    ) => {
      const searchParams = new URLSearchParams();
      if (['vro', 'vssc'].includes(productId) && tenantId) {
        searchParams.append('tenantId', tenantId);
      }

      searchParams.append('getWindowsVms', getWindowsVms ? 'true' : 'false');

      return `${PRODUCT_URL(
        environmentId,
        productId
      )}/deployed-vms?${searchParams.toString()}`;
    };
    export const TOGGLE_FIPS_MODE = (
      environmentId: string,
      productId: string
    ) => `${PRODUCT_URL(environmentId, productId)}/fips`;

    export const UPDATE_NTP_CONFIGURATION = (
      environmentId: string,
      productId: string
    ) => `${PRODUCT_URL(environmentId, productId)}/ntpConfig`;

    export const UPDATE_NTP_CONFIGURATION_PRECHECK = (
      environmentId: string,
      productId: string
    ) => `${PRODUCT_URL(environmentId, productId)}/ntpConfig/pre-validate`;

    export const UPDATE_COLLECTOR_GROUP = (
      environmentId: string,
      productId: string
    ) => `${PRODUCT_URL(environmentId, productId)}/collectorgroups`;

    export const TOGGLE_PENDO_SETTING = (
      environmentId: string,
      productId: string
    ) =>
      `${BASE_URL_V2}/environments/${environmentId}/products/${productId}/toggleVraPendoSetting`;

    export const INSTALL_MANAGEMENT_PACK = (
      environmentId: string,
      productId: string
    ) =>
      `${BASE_URL_V2}/environments/${environmentId}/configureAdapter/${productId}`;

    export const VRLI_LOG_FORWARDING_CONFIGURATION = (
      environmentId: string,
      productId: string,
      vropsLogEntities?: string
    ) => {
      let URI = `${BASE_URL_V2}/environments/${environmentId}/products/${productId}/configureVrliLogForwarding`;
      if (productId === 'vrops' && vropsLogEntities) {
        URI += `?vropsLogEntities=${vropsLogEntities}`;
      }
      return URI;
    };
  }

  export namespace Product {
    export const GET_PRODUCT_CERTIFICATE_URL = (
      productId: string,
      environmentId: string,
      componentName: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/certificate?component=${componentName}`;

    export const GET_LCM_CERTIFICATE_URL = `${BASE_URL}/environments/lcm/products/lcm/certificate`;

    export const PRODUCT_REPLACE_CERTIFICATE_URL = (
      productId: string,
      environmentId: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/updatecertificate`;

    export const LCM_REPLACE_CERTIFICATE_URL = `${BASE_URL}/environments/lcm/products/lcm/updatecertificate`;

    export const PRODUCT_REPLACE_LICENSE_URL = (
      environmentId: string,
      productId: string,
      tenantId?: string
    ) => {
      const searchParams = new URLSearchParams();
      if (['vro', 'vssc'].includes(productId) && tenantId) {
        searchParams.append('tenantId', tenantId);
      }
      return `${BASE_URL}/environments/${environmentId}/products/${productId}/replacelicense?${searchParams.toString()}`;
    };

    export const PRODUCT_REPLACE_CERTIFICATE_PRE_CHECK_URL = (
      productId: string,
      environmentId: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/updatecertificate/prevalidate`;

    export const LCM_REPLACE_CERTIFICATE_PRE_CHECK_URL = `${BASE_URL}/environments/lcm/products/lcm/updatecertificate/prevalidate`;

    export const LCM_SYSTEM_UPGRADE_PRE_CHECK_URL = `${BASE_URL}/v2/system-upgrade/prevalidate`;

    export const DOWNLOAD_PRODUCT_LOGS_URL = (
      environmentId: string,
      productId: string
    ) => `${BASE_URL}/environments/${environmentId}/products/${productId}/logs`;

    export const EXPORT_PRODUCT_CONFIGURATION_URL = (
      environmentId: string,
      productId: string,
      type: string,
      tenantId: string
    ) => {
      const url = `${BASE_URL}/environments/${environmentId}/products/${productId}/configuration?type=${type}`;
      if (['vro', 'vssc'].includes(productId)) {
        const searchParams = new URLSearchParams();
        searchParams.append('tenantId', tenantId);
        return `${url}${searchParams.toString()}`;
      } else {
        return url;
      }
    };

    export const TRIGGER_PRODUCT_INVENTORY_SYNC_URL = (
      environmentId: string,
      productId: string,
      tenantId?: string
    ) => {
      const url = `${BASE_URL}/environments/${environmentId}/products/${productId}/inventorysync?`;
      if (['vro', 'vssc'].includes(productId) && tenantId) {
        const searchParams = new URLSearchParams();
        searchParams.append('tenantId', tenantId);
        return `${url}${searchParams.toString()}`;
      } else {
        return url;
      }
    };

    export const DELETE_PRODUCT_URL = (
      environmentId: string,
      productId: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/delete`;

    export const GET_AVAILABLE_PRODUCT_PATCHES_URL = (
      product: string,
      version: string
    ) =>
      `${BASE_URL}/settings/patchsetting?productId=${product}&productVersion=${version}&availability=DOWNLOADED`;

    export const SUBMIT_PATCH_REQUEST_URL = (
      environmentId: string,
      productId: string,
      patchId: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/patch/${patchId}`;

    export const SUBMIT_SELF_PATCH_REQUEST_URL = (patchId: string) =>
      `${BASE_URL}/selfpatch/patchId/${patchId}`;

    export const APPLY_PSPACK_REQUEST_URL = (psPackID: string) =>
      `${BASE_URL}/pspack/pspackId/${psPackID}`;

    export const SUBMIT_PRODUCT_ACTION_URL = (
      environmentId: string,
      productId: string,
      action: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/${action}`;

    export const MONITOR_VRA_WITH_VROPS_URL = (environmentId: string) =>
      `${BASE_URL}/environments/${environmentId}/monitorvRAwithvROPS`;

    export const CONFIGURE_VROPS_AS_WLP_URL = (environmentId: string) =>
      `${BASE_URL}/environments/${environmentId}/configurevROPSAsWLPEndpoint`;

    export const ADD_DATA_SOURCES_URL = (
      environmentId: string,
      productId: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/adddatasource`;

    export const UPDATE_NODE_PASSWORD_URL = (
      environmentId: string,
      productId: string,
      nodeType: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/node/${nodeType}`;

    export const RETRUST_VIDM_WITH_PRODUCT_URL = (
      environmentId: string,
      productId: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/productVidmRetrust`;

    export const RETRUST_PRODUCT_WITH_LOAD_BALANCER_URL = (
      environmentId: string,
      productId: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/retrustLoadBalancerCertificate`;

    export const GRACEFUL_PRODUCT_SHUTDOWN_URL = (
      environmentId: string,
      productId: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/shutdown`;

    export const GRACEFUL_PRODUCT_POWER_ON_URL = (
      environmentId: string,
      productId: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/powerOn`;

    export const RE_REGISTER_WITH_IDENTITY_MANAGER_URL = (
      environmentId: string,
      productId: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/products/${productId}/productVidmReregister`;

    export const GET_DEPENDENT_PRODUCTS_URL = (
      environmentId: string,
      productId: string
    ) =>
      `${BASE_URL}/productReference/envId/${environmentId}/prodId/${productId}`;

    export const GET_ALL_ENVIRONMENTS_BY_PRODUCT = (
      version: string,
      productId: string
    ) =>
      `${BASE_URL}/environments/prodId/${productId}/prodVersionNAbove/${version}`;

    export const GET_PRODUCT_DEPENDENCIES_URL = (
      environmentId: string,
      productId: string
    ) =>
      `${BASE_URL}/productReference/envId/${environmentId}/prodId/${productId}/reverseLookup`;

    export const GET_DEPENDENT_PRODUCTS_BY_ENV_URL = (environmentId: string) =>
      `${BASE_URL}/productReference/envId/${environmentId}`;

    export const GET_PRODUCT_DEPENDENCIES_BY_ENV_URL = (
      environmentId: string
    ) => `${BASE_URL}/productReference/envId/${environmentId}/reverseLookup`;

    export const GET_PRODUCT_REFERENCE_MAPPING_URL = (
      refType: ProductReferenceType,
      environmentId: string,
      productId: string
    ) =>
      `${BASE_URL}/productReferenceMapping/refType/${refType}/envId/${environmentId}/prodId/${productId}`;

    export const GET_UPGRADE_LCM_REPOSITORY_URL = (
      product: string,
      version: string
    ) => `${BASE_URL}/products/${product}/version/${version}/lcmrepositoryurl`;

    export const GET_COMPATIBILITY_MATRIX = (
      product: string,
      version: string
    ) => `${BASE_URL}/products/${product}/version/${version}/interop`;

    export const GET_PRODUCTS_INTEROP_MATRIX = `${BASE_URL}/products/interop`;

    export const GET_MANUAL_VALIDATIONS_URL = `${BASE_URL}/products/manualprecheck`;

    export const UPGRADE_PRODUCT_URL = `${BASE_URL}/environments/upgrade`;

    export const GET_AVAILABLE_UPGRADE_VERSIONS_URL = `${BASE_URL}/environments/upgrade/version`;

    export const RUN_PRECHECK_UPGRADE_URL = `${BASE_URL}/environments/upgrade/prevalidate`;

    export const APUAT_VALIDATIONS_URL = (
      environmentId: string,
      version: string
    ) =>
      `${BASE_URL}/environments/${environmentId}/upgradeVersion/${version}/vropsapuat`;

    export const GET_UPGRADE_STATE_URL = (requestId: string) =>
      `${BASE_URL}/environments/upgrade/${requestId}/ui`;
  }

  export namespace SettingsV2 {
    export const SETTINGS_URL = `${BASE_URL_V2}/settings`;

    export const PRODUCT_SNMP_URL = `${SETTINGS_URL}/snmp`;

    export const PRODUCT_SNMP_BY_NAME_URL = (snmpName: string) =>
      `${PRODUCT_SNMP_URL}/${snmpName}`;

    export const NOTIFICATION_SMTP_URL = `${SETTINGS_URL}/smtp`;

    export const OUTBOUND_NOTIFICATIONS_URL = `${SETTINGS_URL}/globalSetting`;

    export const CHECK_FOR_UPGRADE_AVAILABILITY_URL = `${BASE_URL_V2}/checkUpgradeLcmVa`;

    export const TRIGGER_UPGRADE_URL = `${BASE_URL_V2}/upgradeLcmVa`;
  }

  export namespace Settings {
    export const UPLOAD_PATCH_URL = `${BASE_URL}/settings/patch/import`;

    export const UPLOAD_PSPACK_URL = `${BASE_URL}/settings/pspack/import`;

    export const GET_NETWORK_INFORMATION_URL = `${BASE_URL}/settings/networksettings`;

    export const GET_SSH_INFORMATION_URL = `${BASE_URL}/settings/newenableDisableSSH`;

    export const GET_SYSTEM_INFORMATION_URL = `${BASE_URL}/settings/systemsettings`;

    export const GET_AVAILABLE_PATCHES_URL = `${BASE_URL}/settings/patchsetting`;

    export const DOWNLOAD_PATCH_URL = `${BASE_URL}/settings/patchdownload`;

    export const DELETE_PATCH_OVA_URL = `${BASE_URL}/settings/productpatchovadelete`;

    export const DELETE_PATCH_URL = `${BASE_URL}/settings/productpatchdelete`;

    export const GET_AGENT_CONFIGURATION_INFORMATION_URL = `${BASE_URL}/settings/loginsightsetting`;

    export const MY_VMWARE_ACCOUNT_URL = `${BASE_URL}/settings/myvmwaresetting`;

    export const UPDATE_MY_VMWARE_ACCOUNT_URL = `${MY_VMWARE_ACCOUNT_URL}/update`;

    export const DELETE_MY_VMWARE_ACCOUNT_URL = `${BASE_URL}/settings/myvmwaresetting/delete`;

    export const NTP_SERVER_URL = `${BASE_URL}/settings/productntpsetting`;

    export const UPDATE_NTP_SERVER_URL = `${NTP_SERVER_URL}/update`;

    export const DELETE_NTP_SERVER_URL = `${BASE_URL}/settings/productntpsetting/delete`;

    export const DNS_SERVER_URL = `${BASE_URL}/settings/productdnssetting`;

    export const UPDATE_DNS_SERVER_URL = `${DNS_SERVER_URL}/update`;

    export const DELETE_DNS_SERVER_URL = `${BASE_URL}/settings/productdnssetting/delete`;

    export const PROXY_URL = `${BASE_URL}/settings/proxysetting`;

    export const EXTENDED_STORAGE_URL = `${BASE_URL}/settings/lcmvadiskexpand`;

    export const SNAPSHOT_URL = `${BASE_URL}/snapshotLcmVa`;

    export const REBOOT_SYSTEM_URL = `${BASE_URL}/settings/lcmvareboot`;

    export const UPDATE_ROOT_PASSWORD_URL = `${BASE_URL}/settings/updatelcmrootpassword`;

    export const DISCOVER_BINARIES_URL = `${BASE_URL}/settings/sourcelocationsetting`;

    export const LCM_UPGRADE_HISTORY = `${BASE_URL}/lcmupgradehistory`;

    export const LCM_SELF_PATCH_HISTORY = `${BASE_URL}/selfpatch/info`;

    export const WINDOWS_ISO_MAPPING = `${BASE_URL}/settings/windowsisosourcemappingsetting`;

    export const GET_MYVMWARE_PRODUCTS_LIST = `${BASE_URL}/settings/myvmwareproductlist`;

    export const MYVMWARE_PRODUCTS_DOWNLOAD = `${BASE_URL}/settings/myvmwaredownload`;

    export const GET_PRODUCT_BINARIES_URL = `${BASE_URL}/settings/productbinarysettings`;

    export const GET_AVAILABLE_POLICIES_URL = `${BASE_URL}/settings/pspacksetting`;

    export const GET_CURRENT_POLICY_URL = `${BASE_URL}/policy/lcm/version`;

    export const DELETE_PRODUCT_BINARY_URL = `${BASE_URL}/settings/productbinarydelete`;

    export const GENERATE_LOG_BUNDLE_URL = `${BASE_URL}/settings/logbundledownload`;

    export const ADD_LCM_NTP_SERVERS_URL = `${BASE_URL}/settings/lcmntpsetting/`;

    export const LCM_NTP_SERVERS_URL = `${BASE_URL}/settings/ntpsetting`;

    export const LCM_TELEMETRY_URL = `${BASE_URL}/settings/telemetrysettings`;

    export const LCM_LOG_AGENT_CONFIGURATION_URL = `${BASE_URL}/settings/loginsightsetting`;

    export const IMPORT_PATCH_OVA_URL = `${BASE_URL}/settings/patch/ova/import`;

    export const GET_AVAILABLE_OVA_BUNDLE_FILES_URL = `${BASE_URL}/settings/patchOvaSourceLocationSetting`;

    export const GET_AVAILABLE_PATCH_FILES_URL = `${BASE_URL}/settings/patchSourceLocationSetting`;

    export const UPLOAD_PRODUCT_PATCH_OVA_URL = `${BASE_URL}/settings/patch/ova/import`;

    export const GET_AVAILABLE_PATCH_OVAS_URL = `${BASE_URL}/settings/productpatchovabinarysettings`;

    export const GET_VCF_MODE_SETTINGS_URL = `${BASE_URL}/settings/vcfmodesettings`;

    export const MINION_MASTERS_URL = `${BASE_URL_V2}/settings/masterconfig`;

    export const MINION_GRAINS_URL = `${BASE_URL_V2}/settings/grains`;

    export const CHECK_MINION_INSTALLED = `${BASE_URL_V2}/settings/checkSaltMinionInstalled`;

    export const INSTALL_MINION = `${BASE_URL_V2}/settings/installsaltminion`;

    export const UPGRADE_MINION = `${BASE_URL_V2}/settings/upgradesaltminion`;
  }

  export namespace Controllers {
    export const SAVE_CONTROLLER_DETAILS = `${BASE_URL}/controller/save`;
    export const GET_ALL_CONTROLLER_DETAILS = `${BASE_URL}/controller/all`;
    export const VALIDATE_CONTROLLER = `${BASE_URL}/controller/validate`;
    export const GET_ALL_CONTROLLER_CLOUDS = `${BASE_URL}/controller/clouds`;
    export const DELETE_LB = `${BASE_URL}/controller/loadbalancer/delete`;
  }

  export const GET_DATA_SOURCE_GROUPS_URL = (version: string) =>
    `/mocks/data-source-groups/${version}/spec.json`;

  export const NOTIFICATION_SMTP_TEST_URL = (email: string) =>
    `${BASE_URL_V2}/testEmail/${email}`;

  export const VALIDATE_WEBHOOK_URL = (type: string, webhook: string) =>
    `${BASE_URL_V2}/validateUrl?webhookType=${type}&webhookUrl=${webhook}`;

  export const ACCOUNT_LOCKOUT_CONFIG_PROPERTY_URL = (key: string) =>
    `/lcm/automata/api/engine/configproperty/${key}`;
}
